import { PrefsScope, prefsStorageKey, readPrefsValue, writePrefsValue } from './prefsStorage';
import { CrudSort, CrudUrlState, EMPTY_URL_STATE } from './urlState';
import { warnInDev } from './warnInDev';

export interface SavedView {
  name: string;
  state: CrudUrlState;
}

/**
 * Tenant-scoped like the other preferences, and the most important of them to scope: a view's
 * name and filter values are the tenant's own data (an employee, a supplier), not just layout.
 */
export function savedViewsKey(resourceKey: string, scope: PrefsScope): string {
  return prefsStorageKey('crud.views', resourceKey, scope);
}

/**
 * The stored array exactly as it is, unreconciled — what every mutation reads and writes.
 * Same guards as readSavedViews: absent localStorage, corrupt JSON and a non-array all
 * read as empty, and it never throws.
 */
export function readStoredViews(resourceKey: string, scope: PrefsScope): unknown[] {
  let raw: unknown;
  try {
    const stored = readPrefsValue('crud.views', resourceKey, scope);
    raw = stored === null ? [] : JSON.parse(stored);
  } catch {
    warnInDev(`could not read saved views for "${resourceKey}"; showing none.`);
    return [];
  }
  return Array.isArray(raw) ? raw : [];
}

// A sort entry the URL codec can write: anything else would throw in applyCrudUrlState
// (`null.field`) or put an unusable `x,SIDEWAYS` into the URL.
function isUsableSort(entry: unknown): entry is CrudSort {
  const candidate = entry as Partial<CrudSort> | null;
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.field === 'string' &&
    candidate.field.trim() !== '' &&
    (candidate.direction === 'ASC' || candidate.direction === 'DESC')
  );
}

/**
 * Stored views for this resource, reconciled against the fields the config
 * currently declares. Same contract as readColumnPrefs: guards absent
 * localStorage, never throws, and degrades rather than discarding everything.
 *
 * For display and apply ONLY. Writing this list back would persist the reconciliation —
 * see upsertSavedView.
 */
export function readSavedViews(resourceKey: string, filterableFields: string[], scope: PrefsScope): SavedView[] {
  const known = new Set(filterableFields);
  const out: SavedView[] = [];
  for (const entry of readStoredViews(resourceKey, scope)) {
    const name = (entry as SavedView)?.name;
    if (typeof name !== 'string' || name.trim() === '') {
      continue;
    }
    const state = { ...EMPTY_URL_STATE, ...(entry as SavedView)?.state };
    const originalFilters = state.filters ?? {};
    const filters = Object.fromEntries(Object.entries(originalFilters).filter(([field]) => known.has(field)));

    // Warn if any filters were dropped
    const droppedFields = Object.keys(originalFilters).filter((field) => !known.has(field));
    if (droppedFields.length > 0) {
      warnInDev(
        `saved view "${name}" for resource "${resourceKey}" dropped unknown filters: ${droppedFields.join(', ')}.`
      );
    }

    // Reconcile search to string type; views never carry a page (always applied at page 1)
    const search = typeof state.search === 'string' ? state.search : '';
    if (typeof state.search !== 'string') {
      warnInDev(`saved view "${name}" for resource "${resourceKey}" has invalid search type; using empty search.`);
    }

    const storedSort: unknown[] = Array.isArray(state.sort) ? state.sort : [];
    const sort = storedSort.filter(isUsableSort);
    const droppedSorts = storedSort.length - sort.length;
    if (droppedSorts > 0) {
      warnInDev(`saved view "${name}" for resource "${resourceKey}" dropped ${droppedSorts} unusable sort entries.`);
    }

    out.push({
      name,
      state: {
        ...state,
        page: 1,
        search,
        filters,
        sort,
      },
    });
  }
  return out;
}

export function writeSavedViews(resourceKey: string, views: SavedView[], scope: PrefsScope): void {
  writeStoredViews(resourceKey, views, scope);
}

function writeStoredViews(resourceKey: string, entries: unknown[], scope: PrefsScope): void {
  writePrefsValue('crud.views', resourceKey, scope, JSON.stringify(entries));
}

const hasName = (entry: unknown, name: string) => (entry as { name?: unknown } | null)?.name === name;

/**
 * Saves a view as a read-modify-write of what is stored NOW, never of a panel's list.
 * Sibling routes share a resource key while declaring different filterable fields (the
 * four Issues tabs), so a reconciled list written back strips the fields a narrower route
 * does not know from every view; and a list read at mount would drop views another browser
 * tab saved since. Saving under an existing name replaces it.
 */
export function upsertSavedView(resourceKey: string, view: SavedView, scope: PrefsScope): void {
  writeStoredViews(
    resourceKey,
    [...readStoredViews(resourceKey, scope).filter((entry) => !hasName(entry, view.name)), view],
    scope
  );
}

/** Deletes by name from what is stored now — every other entry is left exactly as stored. */
export function removeSavedView(resourceKey: string, name: string, scope: PrefsScope): void {
  writeStoredViews(
    resourceKey,
    readStoredViews(resourceKey, scope).filter((entry) => !hasName(entry, name)),
    scope
  );
}
